// ============================================================================
// utils_codec.ts
// Labels et noms de profil H264 + construction du codec négocié WHIP.
// ============================================================================

/** Libellé long — ex: "Baseline (42e01f)" */
export const profileLabel = (p?: string): string => {
  if (!p) return '';
  const l = p.toLowerCase();
  return l.startsWith('42e0') || l.startsWith('4200') ? `Baseline (${p})` :
         l.startsWith('4d00')                         ? `Main (${p})`     :
         l.startsWith('640')                          ? `High (${p})`     : p;
};

/** Alias rétro-compat */
export const profileLabelRegie = profileLabel;

/** Libellé court — ex: "Baseline" */
export const profileName = (profile?: string | null): string | null => {
  if (!profile) return null;
  const p = profile.toLowerCase();
  if (p.startsWith('42e0') || p.startsWith('4200')) return 'Baseline';
  if (p.startsWith('4d00'))                         return 'Main';
  if (p.startsWith('640')  || p.startsWith('6400')) return 'High';
  return profile;
};

/**
 * Libellé complet avec niveau — ex: "Baseline 3.1"
 * (anciennement profileToName dans MediaStreamContext)
 */
export const profileNameFull = (profile?: string | null): string => {
  if (!profile || profile.length < 6) return profile || 'Inconnu';
  const p           = profile.toLowerCase();
  const profileByte = p.slice(0, 2);
  const levelByte   = parseInt(p.slice(4, 6), 16);
  const levelStr    = `${Math.floor(levelByte / 10)}.${levelByte % 10}`;
  if (profileByte === '42') return `Baseline ${levelStr}`;
  if (profileByte === '4d') return `Main ${levelStr}`;
  if (profileByte === '64') return `High ${levelStr}`;
  return `Profil 0x${profileByte} ${levelStr}`;
};

// ── Codec négocié WHIP ────────────────────────────────────────────────────────

export interface NegotiatedCodecInfo {
  codec:         string;
  profile:       string;
  profileName:   string;
  negotiatedAt:  string;
}

/**
 * Construit un objet NegotiatedCodecInfo depuis codec + profile bruts.
 * Déplacé ici depuis MediaStreamContext pour éviter le couplage
 * service API → contexte React.
 */
export const buildNegotiatedCodec = (
  codec: string,
  profile: string
): NegotiatedCodecInfo => ({
  codec,
  profile,
  profileName:  profileNameFull(profile),
  negotiatedAt: new Date().toISOString(),
});

// Retourne la hauteur max encodable pour un profile-level-id H264 donné.
// Ex : "42e01f" → level 0x1F = 31 → 720p
//      "42e028" → level 0x28 = 40 → 1080p
export const h264LevelMaxHeight = (profileLevelId: string | null): number => {
  if (!profileLevelId || profileLevelId.length < 6) return 720; // défaut conservateur
  const level = parseInt(profileLevelId.slice(4, 6), 16);
  if (level >= 0x32) return 2160; // Level 5.0+
  if (level >= 0x28) return 1080; // Level 4.0+
  if (level >= 0x1F) return 720;  // Level 3.1+
  return 480;
};